import type { LocaleLabelKey } from '@/constant/locale-labels'
import { resolveBreadcrumbConfig } from '@/utils/breadcrumb-config'

export interface BackLocation {
  pathname: string
  search: string
}

type Translate = (key: LocaleLabelKey) => string

const normalizePath = (pathname: string) => pathname.replace(/\/+$/, '') || '/'

const isUsableHref = (href: string) => href.startsWith('/') && href !== '#' && !href.includes(':')

/**
 * Nearest breadcrumb ancestor that is a real in-app path.
 * Placeholder hrefs (`#`, unresolved `:param`) are skipped.
 */
export const nearestBreadcrumbParent = (pathname: string, search = '', t?: Translate): string | null => {
  const normalized = normalizePath(pathname)
  const items = resolveBreadcrumbConfig(normalized, t, search)

  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index]
    if (!item?.href || item.isCurrentPage || !isUsableHref(item.href)) continue
    const parent = normalizePath(item.href)
    if (parent !== normalized && parent !== '/') return parent
  }

  return null
}

/** Parent href points back at the current page, so following it would bounce. */
const createsCycle = (pathname: string, parent: string, t?: Translate) => {
  let current = parent
  const seen = new Set<string>([pathname])

  while (!seen.has(current)) {
    seen.add(current)
    const next = nearestBreadcrumbParent(current, '', t)
    if (!next) return false
    if (next === pathname) return true
    current = next
  }

  return false
}

/**
 * Where the header back button should go when it has a recorded visit.
 * The previous page wins, including when it is in another area.
 * Breadcrumb parent is only for a page opened with no in-app history.
 */
export const resolveBackTarget = ({
  pathname,
  search,
  previous,
  t,
}: {
  pathname: string
  search: string
  previous: BackLocation | null
  t?: Translate
}): BackLocation => {
  const normalized = normalizePath(pathname)
  const prior = previous ? { pathname: normalizePath(previous.pathname), search: previous.search } : null

  if (prior && prior.pathname !== normalized) return prior

  const parent = nearestBreadcrumbParent(normalized, search, t)
  if (parent && !createsCycle(normalized, parent, t)) return { pathname: parent, search: '' }

  return { pathname: '/', search: '' }
}
